import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import {
  checkActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { LoginForm } from "./auth.type";
import { auth, db } from "../../../firebase";
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { User, UserType } from "../user/user.type";
import { handleReset, sendTeacherWelcomeEmail, sendSchoolWelcomeEmail, uploadFile } from "../../../services/firebaseService";
import { FirebaseError } from "firebase/app";
import {
  getLatestSubscription,
  isAccountAttachedToSchool,
  isSubscriptionAccessBlocked,
  resolveSubscriptionOwner,
} from "../../subscriptionAccess";


export const AuthApi = createApi({
  reducerPath: "authApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["auth"],
  endpoints: (builder) => ({
    login: builder.mutation<any, LoginForm>({
      async queryFn({ email, password }) {
        try {
          const response = await signInWithEmailAndPassword(
            auth,
            email,
            password
          );
          const user = response.user;
          const userRef = doc(db, "users", user.uid);
          await updateDoc(userRef, {
            lastLoginDate: serverTimestamp(),
          });
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data() as User;
            if (!userData.isActive || userData.archived) {
              await auth.signOut();
              return {
                error: "Votre compte a été désactivé. Veuillez contacter le support.",
                success: false,
              };
            } else {
              const profile = { ...userData, uid: user.uid } as User;
              const owner = await resolveSubscriptionOwner(profile);

              if (isAccountAttachedToSchool(profile, owner)) {
                const subscription = await getLatestSubscription(owner.ownerId);
                if (isSubscriptionAccessBlocked(subscription)) {
                  await auth.signOut();
                  return {
                    error:
                      "La période d’essai ou l’abonnement de votre établissement est terminé. Veuillez contacter votre établissement.",
                    success: false,
                  };
                }
              }

              return { data: response.user, success: true, user };
            }
          } else {
            await auth.signOut();
            return {
              error: "Aucun profil utilisateur trouvé. Veuillez contacter le support.",
              success: false,
            };
          }
          // return { data: response.user, success: true };
        } catch (error: any) {
          return { error: error.message, success: false };
        }
      },
      invalidatesTags: ["auth"],
    }),
    socialLogin: builder.mutation<any, any>({
      async queryFn(provider) {
        try {
          const response = await signInWithPopup(auth, provider);
          // return { data: response.user, success: true };
          const user = response?.user;

          // Vérifier si l'utilisateur existe dans Firestore
          const userRef = doc(db, "users", user?.uid);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            // Créer un nouvel utilisateur s'il n'existe pas
            await setDoc(userRef, {
              uid: user.uid,
              email: user.email,
              fullname: user.displayName || "",
              displayName: user.displayName || "",
              createdAt: serverTimestamp(),
              photoURL: user.photoURL || "",
              provider: provider.providerId,
              userType: "association",
              verified: true,
              completed: false
            });
          }

          return { data: user, success: true };
        } catch (error: any) {
          console.error(error.message);
          return { error: error.message, success: false };
        }
      },
      invalidatesTags: ["auth"],
    }),
    signUp: builder.mutation({
      async queryFn({ email, password, confirmPassword, logoFile, ...data }) {
        try {
          // Étape 1 : Créer l'utilisateur
          const userCredential = await createUserWithEmailAndPassword(
            auth,
            email,
            password
          );
          const user = userCredential.user;
          const userType = data.userType

          // 
          let photoURL = data.photoURL;
          if (logoFile) {
            try {
              photoURL = await uploadFile(logoFile, `medias/${logoFile.name}`);
            } catch (uploadError) {
              console.error("Erreur upload logo:", uploadError);
            }
          }

          // Étape 3 : Stocker le code de validation dans Firestore
          const userRef = doc(db, "users", user.uid);
          await setDoc(
            userRef,
            {
              email,
              verified: false,
              createdAt: serverTimestamp(),
              completed: true,
              ...data,
              photoURL: photoURL || "",
            },
            { merge: true }
          );

          if (userType === UserType.independentTeacher && user.uid) {
            const resp = await sendTeacherWelcomeEmail(email);
            console.log(resp)
          }

          if (userType === UserType.school && user.uid) {
            try {
              await sendSchoolWelcomeEmail(email);
            } catch (mailError) {
              console.error("Échec de l’envoi de l’e-mail de bienvenue établissement :", mailError);
            }
          }


          return {
            data: {
              success: true,
              userId: user.uid,
              message: "Inscription réussie!",
            },
          };
        } catch (error: any) {
          return { error: { message: error.message }, success: false };
        }
      },
    }),
    forgotPassword: builder.mutation({
      queryFn: async ({ email }) => {
        try {
          const response = await handleReset(email);
          return { data: { response, message: "Veuillez consulter votre boîte mail !" } };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
    }),
    validateCode: builder.query({
      queryFn: async (oobCode) => {
        try {
          const response = await checkActionCode(auth, oobCode);
          return { data: response, message: "Code valide" };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
    }),
    resetPassword: builder.mutation({
      queryFn: async ({ oobCode, newPassword }) => {
        try {
          const response = await confirmPasswordReset(
            auth,
            oobCode,
            newPassword
          );
          return {
            data: { response, message: "Mot de passe modifié." },
          };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
    }),
    updatePassword: builder.mutation({
      queryFn: async ({ newPassword }) => {
        const user = auth.currentUser;

        if (user) {
          try {
            const response = await updatePassword(user, newPassword);
            return {
              data: {
                response: response,
                message: "Mot de passe mis à jour.",
              },
            };
          } catch (error: any) {
            return { error: { message: error.message } };
          }
        } else {
          return { error: { message: "Aucun utilisateur connecté." } };
        }
      },
    }),
    sendPasswordResetEmail: builder.mutation({
      queryFn: async ({ email }) => {
        try {
          const response = await sendPasswordResetEmail(auth, email);
          return { data: { response, message: "Email de réinitialisation envoyé." } };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
    }),
    changerPassword: builder.mutation({
      queryFn: async ({ oldPassword, newPassword }) => {
        const currentUser = auth.currentUser;

        if (!currentUser || !currentUser.email) {
          return { error: { status: "AUTH_ERROR", message: "Aucun utilisateur connecté." } };
        }

        try {
          const email = currentUser.email;

          // 1. Ré-authentifier
          const credential = EmailAuthProvider.credential(email, oldPassword);
          await reauthenticateWithCredential(currentUser, credential);

          // 2. Changer le mot de passe
          await updatePassword(currentUser, newPassword);

          // 3. Reconnexion silencieuse pour maintenir la session
          const { user } = await signInWithEmailAndPassword(auth, email, newPassword);

          return {
            data: {
              user,
              success: true,
              message: "Mot de passe mis à jour.",
            },
          };

        } catch (error) {
          console.log("Erreur lors du changement de mot de passe :", error);
          if (error instanceof FirebaseError) {
            const messages: Record<string, string> = {
              "auth/invalid-credential": "Mot de passe actuel incorrect.",
              "auth/requires-recent-login": "Session expirée, veuillez vous reconnecter.",
              "auth/weak-password": "Nouveau mot de passe trop faible (6 caractères minimum).",
              "auth/too-many-requests": "Trop de tentatives, réessayez plus tard.",
            };
            return {
              error: {
                status: "AUTH_ERROR",
                message: messages[error.code] ?? "Une erreur est survenue.",
              },
            };
          }
          return { error: { status: "UNKNOWN_ERROR", message: "Erreur inattendue." } };
        }
      },
    }),

  }),
});

export const {
  useLoginMutation,
  useSocialLoginMutation,
  useSignUpMutation,
  useForgotPasswordMutation,
  useUpdatePasswordMutation,
  useResetPasswordMutation,
  useValidateCodeQuery,
  useSendPasswordResetEmailMutation,
  useChangerPasswordMutation,
} = AuthApi;
